import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Button,
  Group,
  Image,
  LoadingOverlay,
  Modal,
  Paper,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import dayjs from 'dayjs';
import { Icon, LoadMorePagination } from '@/components';
import { SORT_ORDER } from '@/types';
import type { BackgroundImage, BackgroundUpsert } from '@/types/backgrounds';
import { BackgroundModal } from './BackgroundModal.tsx';
import styles from './Backgrounds.module.sass';
import {
  useBackgrounds,
  useCreateBackgroundMutation,
  useRemoveBackgroundMutation,
  useUpdateBackgroundMutation,
} from '@/hooks/backgrounds';
import { backgroundsService } from '@/services/backgrounds.service.ts';

const LAYOUT = {
  TABLE_FILTER_ICON_SIZE: 20,
  BACKGROUNDS_PER_PAGE_LIMIT: 10,
};

export const Backgrounds = () => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editing, setEditing] = useState<BackgroundImage | null>(null);
  const [sort, setSort] = useState<SORT_ORDER>(SORT_ORDER.ASC);
  const [confirmDelete, setConfirmDelete] = useState<BackgroundImage | null>(null);
  const [extraItems, setExtraItems] = useState<BackgroundImage[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

  const queryParams = useMemo(
    () => ({
      limit: LAYOUT.BACKGROUNDS_PER_PAGE_LIMIT,
      sortBy: 'startDate' as const,
      sortOrder: sort,
    }),
    [sort]
  );

  const { data, isLoading } = useBackgrounds(queryParams);
  const createMutation = useCreateBackgroundMutation();
  const updateMutation = useUpdateBackgroundMutation();
  const removeMutation = useRemoveBackgroundMutation();

  const firstPageItems = data?.items ?? [];
  const total = data?.total ?? 0;
  const items = useMemo(() => [...firstPageItems, ...extraItems], [firstPageItems, extraItems]);
  const hasMore = items.length < total;

  useEffect(() => {
    setExtraItems([]);
    if (firstPageItems.length > 0) {
      setCursor(firstPageItems[firstPageItems.length - 1].id);
    } else {
      setCursor(undefined);
    }
  }, [data]);

  const handleOpenNew = () => {
    setEditing(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item: BackgroundImage) => {
    setEditing(item);
    setIsModalOpen(true);
  };

  const handleDelete = (item: BackgroundImage) => {
    setConfirmDelete(item);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    await removeMutation.mutateAsync(confirmDelete.id);
    setConfirmDelete(null);
  };

  const handleSave = async (data: BackgroundUpsert) => {
    if (editing) {
      await updateMutation.mutateAsync({ id: editing.id, data });
    } else {
      await createMutation.mutateAsync(data);
    }
    setIsModalOpen(false);
    setEditing(null);
  };

  const handleSortToggle = () => {
    setSort((prev) => (prev === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC));
  };

  const handleLoadMore = async () => {
    if (!hasMore || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const response = await backgroundsService.list({ ...queryParams, cursor });
      setExtraItems((prev) => [...prev, ...response.items]);
      if (response.items.length > 0) {
        setCursor(response.items[response.items.length - 1].id);
      }
    } catch (error) {
      console.error('Failed to load more backgrounds:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  return (
    <Stack mx="xxl" my="md" gap="md">
      <Group justify="flex-start">
        <Button leftSection={<Icon name="IconPlus" />} onClick={handleOpenNew}>
          New Background
        </Button>
      </Group>

      <Paper radius="md" pos="relative" p="xl">
        <Title order={3} mb="md">
          Backgrounds
        </Title>
        <LoadingOverlay visible={isLoading} />
        <Table striped highlightOnHover withRowBorders withColumnBorders withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Actions</Table.Th>
              <Table.Th>
                <Group gap={4} wrap="nowrap">
                  <Text>Start Date</Text>
                  <ActionIcon variant="subtle" aria-label="toggle sort" onClick={handleSortToggle}>
                    <Icon name={sort === SORT_ORDER.ASC ? 'IconArrowUp' : 'IconArrowDown'} size={16} />
                  </ActionIcon>
                </Group>
              </Table.Th>
              <Table.Th>
                <Group gap={4} wrap="nowrap">
                  <Text>End Date</Text>
                </Group>
              </Table.Th>
              <Table.Th>Thumbnail</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((item) => (
              <Table.Tr key={item.id}>
                <Table.Td className={styles.actionsColumn}>
                  <Group gap="xs">
                    <ActionIcon aria-label="edit" variant="subtle" onClick={() => handleEdit(item)}>
                      <Icon name="IconEdit" size={LAYOUT.TABLE_FILTER_ICON_SIZE} />
                    </ActionIcon>
                    <ActionIcon aria-label="delete" variant="subtle" color="red" onClick={() => handleDelete(item)}>
                      <Icon name="IconTrash" size={LAYOUT.TABLE_FILTER_ICON_SIZE} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text>{dayjs(item.startDate).format('MMM DD YYYY hh:mm A')}</Text>
                </Table.Td>
                <Table.Td>
                  <Text>{dayjs(item.endDate).format('MMM DD YYYY hh:mm A')}</Text>
                </Table.Td>
                <Table.Td>
                  <Image src={item.imageUrl} alt={`Background ${item.id}`} h={80} w={60} fit="cover" radius="sm" />
                </Table.Td>
              </Table.Tr>
            ))}
            {items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text c="dimmed">No backgrounds</Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      <LoadMorePagination
        loaded={items.length}
        total={total}
        hasMore={hasMore}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        onLoadMore={handleLoadMore}
      />

      <BackgroundModal
        opened={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditing(null);
        }}
        id={editing?.id}
        onSave={handleSave}
      />

      <Modal opened={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)} title="Delete background?">
        <Stack>
          <Text>Are you sure you want to delete this background?</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmDelete(null)}>
              No
            </Button>
            <Button color="red" onClick={handleConfirmDelete}>
              Yes
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};
